// El menú "+" de la barra de entrada: adjuntar, las dos entradas que todavía
// no existen y los tres interruptores del turno.
//
// SE OPERA ENTERO CON EL TECLADO. Patrón `menu` de WAI-ARIA con foco rotativo:
// el disparador abre con Enter/Espacio/flecha abajo, las flechas recorren, Home
// y End van a los extremos, Escape cierra y DEVUELVE el foco al "+" (sin eso el
// foco cae a `<body>` y hay que tabular la página entera para volver), y Tab
// cierra dejando que el foco siga su curso normal.
//
// El contenido y la lógica de recorrido viven en `menuMasItems.ts` (puro y
// testeado). Acá solo hay dibujo y eventos.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  BarChart3,
  Check,
  FileSearch,
  Globe,
  Paperclip,
  Plug,
  Sparkles,
} from 'lucide-react';
import {
  esActivable,
  idFocoSiguiente,
  idPrimerFoco,
  idUltimoFoco,
  ITEMS_MENU_MAS,
  ROTULO_PROXIMAMENTE,
  type ItemMenuMas,
} from './menuMasItems';
import type { InterruptoresChat } from '../tipos';

const ICONOS = {
  clip: Paperclip,
  chispa: Sparkles,
  enchufe: Plug,
  'lupa-doc': FileSearch,
  globo: Globe,
  grafico: BarChart3,
} as const;

interface Props {
  abierto: boolean;
  onCerrar: () => void;
  interruptores: InterruptoresChat;
  onCambiarInterruptor: (clave: keyof InterruptoresChat, valor: boolean) => void;
  onAdjuntar: () => void;
  /** Id del elemento disparador, para devolverle el foco al cerrar. */
  disparadorRef: React.RefObject<HTMLButtonElement>;
}

export const MenuMas: React.FC<Props> = ({
  abierto,
  onCerrar,
  interruptores,
  onCambiarInterruptor,
  onAdjuntar,
  disparadorRef,
}) => {
  const [enfocado, setEnfocado] = useState<string | null>(null);
  const contenedorRef = useRef<HTMLDivElement | null>(null);
  const opcionesRef = useRef<Record<string, HTMLButtonElement | null>>({});

  // Al abrir, el foco arranca en el primer ítem. `useLayoutEffect` para que
  // pase en el mismo frame en que el menú aparece: con `useEffect` se veía un
  // parpadeo del anillo de foco saltando desde el "+".
  useLayoutEffect(() => {
    if (!abierto) {
      setEnfocado(null);
      return;
    }
    setEnfocado(idPrimerFoco());
  }, [abierto]);

  // Mover el foco de verdad al ítem marcado (el DOM y el estado tienen que ir
  // juntos: `aria-activedescendant` no alcanza acá porque los ítems son
  // botones reales, no filas de una lista virtual).
  useEffect(() => {
    if (!abierto || !enfocado) return;
    opcionesRef.current[enfocado]?.focus();
  }, [abierto, enfocado]);

  // Cerrar al tocar afuera. `mousedown` y no `click`: si esperáramos al click,
  // un `mousedown` sobre el "+" ya habría disparado su propio toggle y el menú
  // se cerraría y volvería a abrir.
  useEffect(() => {
    if (!abierto) return;
    const afuera = (ev: MouseEvent) => {
      const destino = ev.target as Node;
      if (contenedorRef.current?.contains(destino)) return;
      if (disparadorRef.current?.contains(destino)) return;
      onCerrar();
    };
    document.addEventListener('mousedown', afuera);
    return () => document.removeEventListener('mousedown', afuera);
  }, [abierto, onCerrar, disparadorRef]);

  if (!abierto) return null;

  const cerrarYDevolverFoco = () => {
    onCerrar();
    disparadorRef.current?.focus();
  };

  const activar = (item: ItemMenuMas) => {
    if (item.tipo === 'accion') {
      onAdjuntar();
      cerrarYDevolverFoco();
      return;
    }
    if (item.tipo === 'toggle') {
      // El menú NO se cierra al tocar un interruptor: casi siempre se prende
      // más de uno de una sentada.
      onCambiarInterruptor(item.id, !interruptores[item.id]);
    }
  };

  const alTeclado = (ev: React.KeyboardEvent<HTMLDivElement>) => {
    if (ev.key === 'Escape') {
      ev.preventDefault();
      ev.stopPropagation();
      cerrarYDevolverFoco();
      return;
    }
    if (ev.key === 'Tab') {
      onCerrar();
      return;
    }
    if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
      ev.preventDefault();
      setEnfocado(idFocoSiguiente(enfocado, ev.key === 'ArrowDown' ? 1 : -1));
      return;
    }
    if (ev.key === 'Home') {
      ev.preventDefault();
      setEnfocado(idPrimerFoco());
      return;
    }
    if (ev.key === 'End') {
      ev.preventDefault();
      setEnfocado(idUltimoFoco());
    }
  };

  return (
    <div
      ref={contenedorRef}
      role="menu"
      aria-label="Más opciones"
      onKeyDown={alTeclado}
      // Radios concéntricos: contenedor 16 = fila 12 + 4 de padding.
      className="absolute bottom-full left-0 z-20 mb-2 w-[288px] max-w-[calc(100vw-2rem)] rounded-2xl border border-[#02062B]/[0.08] bg-white p-1 shadow-[0_12px_32px_rgba(2,6,43,0.16)]"
    >
      {ITEMS_MENU_MAS.map((item) => {
        if (item.tipo === 'separador') {
          return (
            <div
              key={item.id}
              role="separator"
              className="my-1 h-px bg-[#02062B]/[0.07]"
            />
          );
        }

        const Icono = ICONOS[item.icono];
        const activo = item.tipo === 'toggle' ? interruptores[item.id] === true : false;
        const deshabilitado = item.tipo === 'proximamente';

        return (
          <button
            key={item.id}
            type="button"
            ref={(el) => {
              opcionesRef.current[item.id] = el;
            }}
            role={item.tipo === 'toggle' ? 'menuitemcheckbox' : 'menuitem'}
            {...(item.tipo === 'toggle' ? { 'aria-checked': activo } : {})}
            aria-disabled={deshabilitado || undefined}
            // Foco rotativo: solo el ítem marcado entra en el orden de tabulación.
            tabIndex={enfocado === item.id ? 0 : -1}
            onFocus={() => setEnfocado(item.id)}
            onClick={() => esActivable(item) && activar(item)}
            className={`flex w-full items-center gap-2.5 rounded-xl py-2 px-3 text-left text-[14px] transition-colors ${
              deshabilitado
                ? // Apagada, NO rota: gris parejo, sin hover, sin rojo, sin
                  // tachado. Lo que la explica es el rótulo "próximamente" que
                  // va a la derecha, no el color.
                  'cursor-default text-[#02062B]/[0.38]'
                : 'text-brand-black hover:bg-[#02062B]/[0.04] focus-visible:bg-[#02062B]/[0.04]'
            }`}
          >
            <Icono
              className={`h-[18px] w-[18px] shrink-0 ${
                deshabilitado
                  ? 'text-[#02062B]/[0.3]'
                  : activo
                    ? 'text-brand-primary'
                    : 'text-[#02062B]/[0.55]'
              }`}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1">
              <span className={`block truncate ${activo ? 'font-semibold' : 'font-medium'}`}>
                {item.etiqueta}
              </span>
              {/* La ayuda es lo que distingue "Investigación" de "Búsqueda web"
                  para alguien que las ve por primera vez. Va siempre, no solo
                  en hover: un tooltip no existe para el teclado ni para touch. */}
              <span
                className={`block truncate text-[12px] ${
                  deshabilitado ? 'text-[#02062B]/[0.32]' : 'text-[#02062B]/[0.5]'
                }`}
              >
                {item.tipo === 'accion'
                  ? 'Imágenes o archivos de texto'
                  : item.ayuda}
              </span>
            </span>

            {item.tipo === 'proximamente' && (
              <span className="shrink-0 rounded-full bg-[#02062B]/[0.05] py-0.5 px-2 text-[11px] font-medium text-[#02062B]/[0.45]">
                {ROTULO_PROXIMAMENTE}
              </span>
            )}

            {item.tipo === 'toggle' && (
              // La marca de activo no es solo color: es una casilla que se
              // llena y una tilde. Y `aria-checked` lo dice aparte.
              <span
                aria-hidden="true"
                className={`inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-md border transition-colors ${
                  activo
                    ? 'border-brand-primary bg-brand-primary text-white'
                    : 'border-[#02062B]/[0.18] bg-white'
                }`}
              >
                {activo && <Check className="h-3 w-3" strokeWidth={3} />}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default MenuMas;
